import { z } from 'zod';
import { saleModeSchema } from './sale';
import {
  bodyTypeSchema,
  checklistSchema,
  equipmentSelectionSchema,
  expertiseReportSchema,
  fuelSchema,
  gearSchema,
  MAX_MIRROR_URLS,
  mediaMirrorResultSchema,
  vehicleConditionSchema,
  vehicleTypeSchema,
} from './vehicle';

/**
 * Vehicle-ingestion API shapes (M4.6). Partner **firms** push their own vehicles into planned
 * auctions via a per-firm API key (`X-Api-Key`); an admin moderates each submission. Mirrors
 * the `company_api_keys` / `vehicle_submissions` tables (`docs/03-data-model.md`) and the
 * endpoints in `docs/04-api-contract.md`; see ADR 0017 and ADR 0026 (the per-firm identity).
 * Money is integer minor units (kuruş); timestamps are epoch-ms integers on the wire.
 */

// --- API keys (issued by an admin from the firm's page) ---------------------

/** `POST /admin/companies/:id/api-keys` — mint a key. The plaintext is returned exactly once. */
export const apiKeyCreateSchema = z.object({
  label: z.string().max(120).optional(), // optional human label
});
export type ApiKeyCreateInput = z.infer<typeof apiKeyCreateSchema>;

/** Key metadata projection — never carries the secret (only its hash is stored). */
export const apiKeySummarySchema = z.object({
  id: z.uuid(),
  label: z.string().nullable(),
  createdAt: z.number().int(), // epoch-ms
  lastUsedAt: z.number().int().nullable(), // epoch-ms
  revokedAt: z.number().int().nullable(), // epoch-ms
});
export type ApiKeySummary = z.infer<typeof apiKeySummarySchema>;

/** Mint response — the metadata plus the one-time plaintext `key`. */
export const apiKeyCreatedSchema = apiKeySummarySchema.extend({
  key: z.string(), // plaintext, shown ONCE — store it now, it is not recoverable
});
export type ApiKeyCreated = z.infer<typeof apiKeyCreatedSchema>;

// --- Vehicle submission (X-Api-Key) -----------------------------------------

/**
 * `POST /client/vehicles` — submit a vehicle into a chosen planned auction. `startingPrice`
 * is the (public) opening price and doubles as the auction-eligibility gate (> 0);
 * `reserve` is the hidden reserve default. The submission lands `queued` pending admin
 * moderation — it becomes a `SCHEDULED` lot only on approve.
 */
export const vehicleSubmitSchema = z.object({
  auctionId: z.uuid(), // a PUBLISHED sale from GET /client/auctions/planned
  clientRef: z.string().max(120).optional(), // the client's own reference
  plate: z.string().min(1).max(32),
  chassis: z.string().max(64).optional(),
  brand: z.string().max(120).optional(),
  series: z.string().max(120).optional(),
  properties: z.record(z.string(), z.unknown()).optional(), // free-form JSON object
  startingPrice: z.number().int().positive(), // kuruş; opening price + eligibility gate
  reserve: z.number().int().positive().optional(), // kuruş; hidden reserve default
  saleMode: saleModeSchema.optional(), // defaults to 'normal'
  increment: z.number().int().positive().optional(), // kuruş; flat step (defaults to 10000)
  currency: z.string().max(8).optional(), // defaults to 'TRY'

  // M11 vehicle detail (ADR 0025). All optional, so every existing integration keeps
  // working untouched — a client that sends none of this gets exactly the old behaviour.
  // NOTE `modelName` is the trim; `properties` above keeps its existing meaning (free-form
  // client extras), and is finally read back by the UI as of M11.
  modelName: z.string().max(200).optional(),
  year: z.number().int().min(1900).max(2200).optional(),
  color: z.string().max(60).optional(),
  km: z.number().int().min(0).optional(),
  condition: vehicleConditionSchema.optional(),
  fuel: fuelSchema.optional(),
  gear: gearSchema.optional(),
  bodyType: bodyTypeSchema.optional(),
  vehicleType: vehicleTypeSchema.optional(),
  engineCapacity: z.number().int().min(0).optional(), // cc
  enginePower: z.number().int().min(0).optional(), // hp
  description: z.string().max(5000).optional(),
  equipments: equipmentSelectionSchema.optional(),
  checklist: checklistSchema.optional(),
  expertise: expertiseReportSchema.optional(),

  /**
   * Photo URLs to mirror into our own storage. Fetched **after** the submission commits, so a
   * client whose CDN 404s does not lose the whole submission; per-URL outcomes come back on
   * the fetch-status projection. We keep a copy rather than hot-linking so a client rotating
   * or deleting its CDN cannot blank an auction that is already live.
   */
  mediaUrls: z.array(z.url()).max(MAX_MIRROR_URLS).optional(),
});
export type VehicleSubmitInput = z.infer<typeof vehicleSubmitSchema>;

/** `PATCH /client/vehicles/:id` — edit a still-`queued` submission's vehicle. All optional. */
export const vehiclePatchSchema = vehicleSubmitSchema
  .omit({ auctionId: true, saleMode: true })
  .partial();
export type VehiclePatchInput = z.infer<typeof vehiclePatchSchema>;

// --- Read projections -------------------------------------------------------

/**
 * The public status a client sees for its submission. `queued`/`rejected` come from the
 * moderation record; `scheduled|live|sold|unsold` are DERIVED from the created lot's state
 * (SCHEDULED→scheduled, OPEN→live, SOLD→sold, UNSOLD→unsold).
 */
export const submissionStatusSchema = z.enum([
  'queued',
  'scheduled',
  'live',
  'sold',
  'unsold',
  'rejected',
]);
export type SubmissionStatus = z.infer<typeof submissionStatusSchema>;

/** Submission projection returned to the client (create + fetch-status). */
export const submissionViewSchema = z.object({
  id: z.uuid(),
  vehicleId: z.uuid(),
  saleId: z.uuid(),
  clientRef: z.string().nullable(),
  status: submissionStatusSchema,
  lotId: z.uuid().nullable(), // set once approved into a lot
  rejectReason: z.string().nullable(),
  createdAt: z.number().int(), // epoch-ms
  decidedAt: z.number().int().nullable(), // epoch-ms
  /**
   * Per-URL outcome of the `mediaUrls` mirror pass (M11). Absent when the submit carried no
   * URLs; present with `ok: false` entries when some could not be fetched — the submission
   * itself still stands, so this is how a client learns its photos did not arrive.
   */
  media: z.array(mediaMirrorResultSchema).optional(),
});
export type SubmissionView = z.infer<typeof submissionViewSchema>;

/** `POST /admin/submissions/:id/reject` body. */
export const submissionRejectSchema = z.object({
  reason: z.string().max(500).optional(),
});
export type SubmissionRejectInput = z.infer<typeof submissionRejectSchema>;
